/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

let _lastVariant = null;

const getProductRoot = (formOrElement) => {
  if (!formOrElement) return document.querySelector('.product');
  if (formOrElement.classList && formOrElement.classList.contains('product')) return formOrElement;
  return formOrElement.closest('.product') || document.querySelector('.product');
};

const parseJsonSafe = (value, fallback = null) => {
  try {
    return JSON.parse(value);
  } catch (e) {
    return fallback;
  }
};

const getVariantInventoryMap = (productRoot) => {
  const $inventory = productRoot && productRoot.querySelector('[data-variant-inventory]');
  if (!$inventory) return {};
  return parseJsonSafe($inventory.textContent, {}) || {};
};

const mergeVariantInventory = (variant, productRoot) => {
  if (!variant || !variant.id) return variant;
  const inventoryMap = getVariantInventoryMap(productRoot);
  const inventory = inventoryMap[variant.id] || inventoryMap[String(variant.id)];
  if (!inventory) return variant;
  return Object.assign({}, variant, {
    inventory_quantity: inventory.inventory_quantity,
    inventory_policy: inventory.inventory_policy
  });
};

const syncVariantStatusProperty = (form) => {
  if (!form) return;
  const $hiddenStatus = form.querySelector('#variant-status') || form.querySelector('[name="properties[_variant-status]"]');
  if (!$hiddenStatus) return;
  if ($hiddenStatus.value && $hiddenStatus.value.trim() !== '') return;

  const productRoot = getProductRoot(form);
  const $variantStatusNote = productRoot && productRoot.querySelector('.variant-status-note');
  if (!$variantStatusNote) return;

  const message = ($variantStatusNote.dataset.message || $variantStatusNote.textContent || '').trim();
  if (message) $hiddenStatus.value = message;
};

const UpdateVariantStatus = (variant, root) => {
  _lastVariant = variant;
  const productRoot = root || document.querySelector('.product');
  if (!productRoot || !variant) return;

  variant = mergeVariantInventory(variant, productRoot);

  const CLASS_HIDE = 'visually-hidden';
  const ICONS = {
    in_stock: variant_status_settings.icon_in_stock,
    special_order: variant_status_settings.icon_special_order,
    coming_soon: variant_status_settings.icon_coming_soon,
    pre_order: variant_status_settings.icon_pre_order
  };

  let $variantStatus = productRoot.querySelector('.variant-status');
  if (!$variantStatus) return;

  let allow_oversell = variant.inventory_policy == 'continue';
  let variant_type = $variantStatus.dataset.variantStatusType;

  let type = {
    preorder: variant_type == 'pre-order',
    special_order: variant_type == 'special-order',
    coming_soon: variant_type == 'coming-soon'
  };

  let variant_status = {
    normal: {
      in_stock: variant.available,
      out_of_stock: !allow_oversell && !variant.available
    },
    pre_order: {
      in_stock: type.preorder && variant.available,
      out_of_stock: type.preorder && !variant.available
    },
    special_order: {
      in_stock: type.special_order && allow_oversell && variant.inventory_quantity <= 0 && variant.available,
      out_of_stock: type.special_order && !variant.available
    }
  };

  let isAvailable = variant_status.normal.in_stock ||
    variant_status.pre_order.in_stock ||
    variant_status.special_order.in_stock;
  let coming_soon = type.coming_soon;

  let $variantStatusNote = $variantStatus.querySelector('.variant-status-note');
  let $variantDrawerWrap = $variantStatus.querySelector('.variant-drawer-wrap');

  if ($variantDrawerWrap) $variantDrawerWrap.classList.add(CLASS_HIDE);

  if ($variantStatusNote) {
    let variant_status_msg = $variantStatusNote.dataset.message || '';
    let variant_status_html = `<span>${variant_status_msg}</span>`;

    if (variant_status.special_order.in_stock) {
      variant_status_html = ICONS.special_order + `<span>${variant_status_msg}</span>`;
      if ($variantDrawerWrap) $variantDrawerWrap.classList.remove(CLASS_HIDE);
    }
    else if (variant_status.pre_order.in_stock) {
      variant_status_html = ICONS.pre_order + `<span>${variant_status_msg}</span>`;
      if ($variantDrawerWrap) $variantDrawerWrap.classList.remove(CLASS_HIDE);
    }
    else if (variant_status.normal.in_stock) {
      variant_status_msg = 'In Stock';
      variant_status_html = ICONS.in_stock + `<span>${variant_status_msg}</span>`;
    }
    else if (coming_soon) {
      variant_status_html = ICONS.coming_soon + `<span>${variant_status_msg}</span>`;
    }

    let $productForm = productRoot.querySelector('.shopify-product-form');
    let $hiddenStatus = $productForm ? $productForm.querySelector('#variant-status') : null;
    if ($hiddenStatus) $hiddenStatus.value = variant_status_msg;

    $variantStatusNote.innerHTML = `<div style="display:flex; align-items:center;">${variant_status_html}</div>`;
    $variantStatus.classList[(isAvailable || coming_soon) ? 'remove' : 'add'](CLASS_HIDE);
  }

  let btn_text;
  if (isAvailable) {
    if (variant_status.pre_order.in_stock) btn_text = 'PRE-ORDER';
    else if (variant_status.special_order.in_stock) btn_text = 'SPECIAL ORDER';
    else btn_text = 'ADD TO CART';
  } else {
    btn_text = (variant_status.pre_order.out_of_stock ? 'PRE-ORDER ' : '') + 'SOLD OUT';
  }
  if (coming_soon && !isAvailable) btn_text = 'COMING SOON';

  let $productForm = productRoot.querySelector('.shopify-product-form');
  let $atcButtons = [...productRoot.querySelectorAll('[data-product-add-to-cart-button]')];

  if ($productForm) {
    $atcButtons.push(...document.querySelectorAll(`[data-product-add-to-cart-button][form="${$productForm.id}"]`));
  }

  [...new Set($atcButtons)].forEach(($btn_ATC) => {
    let $btnText = $btn_ATC.querySelector('.loader-button__text');
    if ($btnText) $btnText.innerText = btn_text;

    if (coming_soon && !isAvailable) {
      $btn_ATC.disabled = true;
    } else if (isAvailable) {
      $btn_ATC.disabled = false;
    } else {
      $btn_ATC.disabled = true;
    }
  });
};

(function () {
  document.addEventListener('variant:changed', function (event) {
    UpdateVariantStatus(event.detail.variant, getProductRoot(event.target));
  });

  document.addEventListener('submit', function (event) {
    const form = event.target;
    if (!form || !form.matches || !form.matches('form[is="product-form"], form.shopify-product-form')) return;
    syncVariantStatusProperty(form);
  }, true);

  document.addEventListener('DOMContentLoaded', () => {
    let $variant = document.querySelector('[data-selected-variant-json]');
    if ($variant) {
      const variant = parseJsonSafe($variant.value);
      if (variant) {
        UpdateVariantStatus(variant, getProductRoot($variant.closest('.product')));
      }
    }

    document.querySelectorAll('.shopify-product-form').forEach(($form) => {
      $form.addEventListener('product:rerender', () => {
        if (_lastVariant) {
          UpdateVariantStatus(_lastVariant, getProductRoot($form));
        }
      });
    });
  });
})();